import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, ChevronLeft, ChevronRight, Clock3, Eye, FileText, RefreshCw } from 'lucide-react'
import apiClient from '../../api/axiosClient'

const viewedMessage = 'You have already viewed this bill. If you need to access it again, please contact the Insurance Manager'
const pageSize = 6

const AdminDashboard = () => {
  const [bills, setBills] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const loadBills = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await apiClient.get('/insurance/ip-final-bills', {
        params: {
          page: 1,
          per_page: 200
        }
      })
      setBills(response.data.receipts || [])
      setCurrentPage(1)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load IP final bills')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBills()
  }, [])

  const viewBill = async (bill) => {
    if (bill.viewed_by_current_user) return

    try {
      const response = await apiClient.get(`/insurance/receipts/${bill.receipt_id}/download`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)

      await apiClient.post(`/insurance/ip-final-bills/${bill.receipt_id}/viewed`)
      setBills((prev) => prev.map((item) => (
        item.receipt_id === bill.receipt_id
          ? { ...item, viewed_by_current_user: true }
          : item
      )))
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to view IP final bill')
    }
  }

  const viewedCount = bills.filter((bill) => bill.viewed_by_current_user).length
  const pendingCount = bills.length - viewedCount
  const totalPages = Math.max(1, Math.ceil(bills.length / pageSize))
  const paginatedBills = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return bills.slice(start, start + pageSize)
  }, [bills, currentPage])
  const pageStart = bills.length === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const pageEnd = Math.min(currentPage * pageSize, bills.length)

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages))
  }

  const stats = [
    {
      label: 'Total IP Final Bills',
      value: bills.length,
      icon: FileText,
      accent: 'text-blue-700',
      bg: 'bg-blue-50',
    },
    {
      label: 'Viewed Count',
      value: viewedCount,
      icon: CheckCircle2,
      accent: 'text-emerald-700',
      bg: 'bg-emerald-50',
    },
    {
      label: 'Pending View',
      value: pendingCount,
      icon: Clock3,
      accent: 'text-amber-700',
      bg: 'bg-amber-50',
    },
  ]

  return (
    <div className="bg-white rounded-xl p-6 shadow-md space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Insurance Admin Dashboard</h2>
          <p className="text-gray-600 mt-1">IP Final Bills are view-only for Insurance Admin.</p>
        </div>
        <button
          type="button"
          onClick={loadBills}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {stats.map((stat) => (
          <div key={stat.label} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-600">{stat.label}</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</div>
            </div>
            <div className={`h-11 w-11 rounded-lg ${stat.bg} ${stat.accent} flex items-center justify-center`}>
              <stat.icon size={22} />
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Receipt No</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Patient</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-600">Loading IP final bills...</td>
              </tr>
            ) : bills.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-600">No IP final bills found</td>
              </tr>
            ) : (
              paginatedBills.map((bill) => (
                <tr key={bill.receipt_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{bill.receipt_no}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{bill.patient?.full_name || 'N/A'}</div>
                      <div className="text-gray-500">{bill.patient?.uhid_no || 'N/A'}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div className="text-gray-900">{bill.receipt_date}</div>
                      <div className="text-gray-500">{bill.receipt_time}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{bill.total_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => viewBill(bill)}
                        disabled={bill.viewed_by_current_user}
                        className="p-1 text-purple-600 hover:bg-purple-100 rounded transition-colors disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                        title={bill.viewed_by_current_user ? viewedMessage : 'View PDF'}
                      >
                        <Eye size={16} />
                      </button>
                      {bill.viewed_by_current_user && (
                        <span className="text-xs text-red-600">{viewedMessage}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {bills.length > pageSize && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <div className="text-sm text-gray-600">
              Showing {pageStart}-{pageEnd} of {bills.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => goToPage(page)}
                  className={`h-9 min-w-9 px-3 rounded-lg text-sm font-medium ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard
